import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { Ic, Modal, useToast, LoadingSpinner, btnGhost, btnPrimary, btnSecondary, inputStyle, showStyledAlert, DomainTag, StatusBadge, DAYS_HEB } from "./components/UI";
import { getVolunteers, getDomains, getActivities, getRecurringActivities, getUsers, getBeneficiaries, getBeneficiariesForVolunteer, getContributionTypes, seedInitialData, syncRecurringActivitiesUntilToday } from "./utils/firestore";
import { collection, onSnapshot, query, where } from "firebase/firestore";
import { db } from "./firebase";
import LoginPage from "./pages/LoginPage";
import DashboardPage from "./pages/DashboardPage";
import VolunteersPage from "./pages/VolunteersPage";
import ActivitiesPage from "./pages/ActivitiesPage";
import RecurringPage from "./pages/RecurringPage";
import DomainsPage from "./pages/DomainsPage";
import BeneficiariesPage from "./pages/BeneficiariesPage";
import PortalPage from "./pages/PortalPage";
import UsersPage from "./pages/UsersPage";
import ReportsPage from "./pages/ReportsPage";
import JournalPage from "./pages/JournalPage";
import ContributionsPage from "./pages/ContributionsPage";
import DevelopmentNotesPage from "./pages/DevelopmentNotesPage";
import { getVolunteerCoordinatorIds, getVolunteerTeamLeadIds } from "./utils/volunteerAssignments";
import { formatContributionCurrency, getActivityContributionSummary, getActivityContributionValueTotal, sumActivitiesContributionTotal, sumActivitiesContributionValueTotal } from "./utils/activityContributions";

const css = `
@import url('https://fonts.googleapis.com/css2?family=Heebo:wght@300;400;500;600;700;800;900&family=Assistant:wght@300;400;500;600;700;800&display=swap');
*{margin:0;padding:0;box-sizing:border-box}
body,html,#root{font-family:'Heebo',sans-serif;direction:rtl;background:#FAFAFA;color:#262626;height:100%;overflow:hidden}
input,select,textarea,button{font-family:'Heebo',sans-serif}
::-webkit-scrollbar{width:5px}::-webkit-scrollbar-track{background:transparent}::-webkit-scrollbar-thumb{background:#D4D4D4;border-radius:3px}
@keyframes spin{from{transform:rotate(0deg)}to{transform:rotate(360deg)}}
`;

const BIRTHDAY_DONE_STORAGE_KEY = "ezer-birthday-done-v1";
const PENDING_RECURRING_ALERT_KEY = "ezer-pending-recurring-alert-dismissed-v1";
const APP_HISTORY_MARKER = "ezer-mizion-app-route";
const COORDINATOR_ACTION_DEFAULTS = {
  addVolunteers: true,
  addActivities: true,
  addRecurring: true,
  addContributionTypes: true,
  editDomains: true,
  addBeneficiaries: true,
  manageUsers: false,
  viewReports: true,
  viewDevelopmentNotes: true,
};
const VOLUNTEER_ACTION_DEFAULTS = {
  viewDevelopmentNotes: true,
};

function normalizeHebrewText(value) {
  return (value || "")
    .toString()
    .trim()
    .toLowerCase()
    .replace(/['"׳״]/g, "")
    .replace(/\s+/g, " ");
}

function toHebrewDayLetters(num) {
  if (!Number.isFinite(num) || num < 1 || num > 30) return String(num || "");
  const units = ["", "א", "ב", "ג", "ד", "ה", "ו", "ז", "ח", "ט"];
  if (num <= 9) return units[num];
  if (num === 10) return "י";
  if (num === 11) return "יא";
  if (num === 12) return "יב";
  if (num === 13) return "יג";
  if (num === 14) return "יד";
  if (num === 15) return "טו";
  if (num === 16) return "טז";
  if (num === 17) return "יז";
  if (num === 18) return "יח";
  if (num === 19) return "יט";
  if (num === 20) return "כ";
  if (num <= 29) return `כ${units[num - 20]}`;
  return "ל";
}

function hebrewDateParts(date) {
  const parts = new Intl.DateTimeFormat("he-IL-u-ca-hebrew", {
    day: "numeric",
    month: "long",
    year: "numeric",
  }).formatToParts(date);
  return {
    dayNum: Number(parts.find((p) => p.type === "day")?.value || 0),
    month: parts.find((p) => p.type === "month")?.value || "",
    year: parts.find((p) => p.type === "year")?.value || "",
  };
}

function buildUpcomingHebrewDates(today = new Date(), daysAhead = 7) {
  const upcoming = [];
  for (let i = 0; i <= daysAhead; i += 1) {
    const d = new Date(today);
    d.setDate(today.getDate() + i);
    const h = hebrewDateParts(d);
    const dayLetters = toHebrewDayLetters(h.dayNum);
    const keyDate = normalizeHebrewText(`${dayLetters} ${h.month}`);
    upcoming.push({
      keyDate,
      keyDay: normalizeHebrewText(String(h.dayNum)),
      year: h.year,
    });
  }
  return upcoming;
}

function findUpcomingBirthdayMatch(volunteer, upcomingDates) {
  const hb = normalizeHebrewText(volunteer?.hebrewBirthday || "");
  if (!hb) return null;
  return upcomingDates.find((u) => {
    const [dayToken, monthToken] = u.keyDate.split(" ");
    return hb.includes(normalizeHebrewText(monthToken)) && (hb.includes(dayToken) || hb.includes(u.keyDay));
  }) || null;
}

function fullVolunteerName(v) {
  return (v?.name || `${v?.firstName || ""} ${v?.lastName || ""}`.trim() || "").trim();
}

function NavCounter({ count, active = false }) {
  if (!count) return null;
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        minWidth: 22,
        height: 22,
        padding: "0 7px",
        borderRadius: 999,
        background: active ? "white" : "#C41E3A",
        color: active ? "#C41E3A" : "white",
        fontSize: 12,
        fontWeight: 800,
        lineHeight: 1,
        flexShrink: 0,
      }}
    >
      {count}
    </span>
  );
}

function VolDetailInline({ volId, volunteers, domains, activities, recurring, onBack }) {
  const vol = volunteers.find(v => v.id === volId);
  const [tab, setTab] = useState("history");
  if (!vol) return <div style={{ textAlign: "center", padding: 48, color: "#A3A3A3" }}>מתנדב לא נמצא</div>;
  const volunteerModeLabel = (vol?.engagementType || "fixed") === "fixed" ? "קבוע" : "מזדמן";

  const now = new Date();
  const weekAgo = new Date(now); weekAgo.setDate(weekAgo.getDate() - 7);
  const monthAgo = new Date(now); monthAgo.setDate(monthAgo.getDate() - 30);
  const wk = activities.filter(a => a.volunteerId === vol.id && new Date(a.date) >= weekAgo).reduce((s, a) => s + Number(a.duration), 0);
  const mo = activities.filter(a => a.volunteerId === vol.id && new Date(a.date) >= monthAgo).reduce((s, a) => s + Number(a.duration), 0);
  const total = activities.filter(a => a.volunteerId === vol.id).reduce((s, a) => s + Number(a.duration), 0);
  const acts = activities.filter(a => a.volunteerId === vol.id).sort((a, b) => (b.date || "").localeCompare(a.date || ""));
  const totalContributions = sumActivitiesContributionTotal(acts);
  const totalContributionValue = sumActivitiesContributionValueTotal(acts);
  const recs = recurring.filter(r => r.volunteerId === vol.id);
  const domMap = {}; domains.forEach(d => { domMap[d.id] = d; });

  const thStyle = { padding: "12px 16px", fontSize: 12, fontWeight: 600, color: "#737373", textAlign: "right", background: "#FAFAFA", borderBottom: "1px solid #E8E8E8" };
  const tdStyle = { padding: "14px 16px", borderBottom: "1px solid #F5F5F5", fontSize: 14 };

  return (
    <div>
      <button style={{ display: "inline-flex", alignItems: "center", gap: 8, padding: "6px 12px", borderRadius: 10, fontSize: 13, cursor: "pointer", border: "1px solid #E8E8E8", background: "#F5F5F5", color: "#404040", fontFamily: "Heebo", marginBottom: 16 }} onClick={onBack}><Ic.Back />חזרה</button>

      <div style={{ background: "white", borderRadius: 14, border: "1px solid #E8E8E8", padding: 24, marginBottom: 24 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 20, marginBottom: 24, paddingBottom: 20, borderBottom: "1px solid #E8E8E8" }}>
          <div style={{ width: 72, height: 72, borderRadius: 16, background: "linear-gradient(135deg,#C41E3A,#E8364F)", display: "flex", alignItems: "center", justifyContent: "center", color: "white", fontSize: 26, fontWeight: 800, fontFamily: "Assistant", flexShrink: 0 }}>{vol.name?.[0]}</div>
          <div style={{ flex: 1 }}>
            <h2 style={{ fontFamily: "Assistant", fontSize: 24, fontWeight: 800, marginBottom: 4 }}>{vol.name}</h2>
            <div style={{ display: "flex", alignItems: "center", gap: 16, color: "#737373", fontSize: 14, flexWrap: "wrap" }}>
              {vol.phone && <span style={{ display: "flex", alignItems: "center", gap: 4 }}><Ic.Phone />{vol.phone}</span>}
              {vol.email && <span style={{ display: "flex", alignItems: "center", gap: 4 }}><Ic.Mail />{vol.email}</span>}
              <span style={{ display: "inline-flex", alignItems: "center", padding: "3px 10px", borderRadius: 999, border: "1px solid #E5E7EB", background: "#F8FAFC", color: "#475569", fontSize: 12, fontWeight: 600 }}>
                {volunteerModeLabel}
              </span>
              <StatusBadge status={vol.status} />
            </div>
          </div>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(5,1fr)", gap: 12, marginBottom: 20 }}>
          {[[wk, "שעות השבוע"], [mo, "שעות החודש"], [total, "סך הכל"], [totalContributions, "תרומות"], [formatContributionCurrency(totalContributionValue) || "—", "שווי תרומות"]].map(([v, l], i) => (
            <div key={i} style={{ background: "#FAFAFA", borderRadius: 12, padding: 16, textAlign: "center" }}>
              <div style={{ fontFamily: "Assistant", fontSize: 24, fontWeight: 800, color: "#C41E3A" }}>{typeof v === "number" && l !== "תרומות" ? v.toFixed(1) : v}</div>
              <div style={{ fontSize: 12, color: "#737373", marginTop: 2 }}>{l}</div>
            </div>
          ))}
        </div>
        <div style={{ marginBottom: 12 }}>
          <div style={{ fontFamily: "Assistant", fontSize: 16, fontWeight: 700, marginBottom: 8 }}>תחומי פעילות</div>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>{(vol.domains || []).map(did => domMap[did] ? <DomainTag key={did} domain={domMap[did]} /> : null)}</div>
        </div>
        {vol.notes && <div><div style={{ fontFamily: "Assistant", fontSize: 16, fontWeight: 700, marginBottom: 4 }}>הערות</div><p style={{ fontSize: 14, color: "#525252" }}>{vol.notes}</p></div>}
      </div>

      <div style={{ display: "flex", gap: 4, marginBottom: 20, borderBottom: "2px solid #E8E8E8" }}>
        {[["history", "היסטוריה"], ["recurring", "פעילויות קבועות"]].map(([id, label]) => (
          <button key={id} onClick={() => setTab(id)} style={{ padding: "10px 18px", fontSize: 14, fontWeight: tab === id ? 600 : 500, color: tab === id ? "#C41E3A" : "#737373", cursor: "pointer", border: "none", background: "none", borderBottom: tab === id ? "2px solid #C41E3A" : "2px solid transparent", marginBottom: -2, fontFamily: "Heebo" }}>{label}</button>
        ))}
      </div>

      <div style={{ background: "white", borderRadius: 14, border: "1px solid #E8E8E8", overflow: "hidden" }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead><tr>
            {(tab === "history" ? ["תאריך", "תחום", "שעה", "משך", "תרומות", "שווי"] : ["יום", "תחום", "שעה", "משך", "סטטוס"]).map(h => <th key={h} style={thStyle}>{h}</th>)}
          </tr></thead>
          <tbody>
            {tab === "history" ? acts.slice(0, 25).map(a => (
              <tr key={a.id}>
                <td style={tdStyle}>{a.date}</td>
                <td style={tdStyle}>{domMap[a.domainId] ? <DomainTag domain={domMap[a.domainId]} /> : "—"}</td>
                <td style={tdStyle}>{a.time}</td>
                <td style={tdStyle}>{a.duration} שעות</td>
                <td style={tdStyle}>{getActivityContributionSummary(a) || "—"}</td>
                <td style={tdStyle}>{formatContributionCurrency(getActivityContributionValueTotal(a)) || "—"}</td>
              </tr>
            )) : recs.map(r => (
              <tr key={r.id}>
                <td style={tdStyle}>יום {DAYS_HEB[r.day]}</td>
                <td style={tdStyle}>{domMap[r.domainId] ? <DomainTag domain={domMap[r.domainId]} /> : "—"}</td>
                <td style={tdStyle}>{r.time}</td>
                <td style={tdStyle}>{r.duration} שעות</td>
                <td style={tdStyle}><StatusBadge status={r.status} /></td>
              </tr>
            ))}
            {((tab === "history" && acts.length === 0) || (tab === "recurring" && recs.length === 0)) && <tr><td colSpan={tab === "history" ? 6 : 5} style={{ textAlign: "center", padding: 32, color: "#A3A3A3" }}>אין נתונים</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function AppContent() {
  const { currentUser, userData, logout, updateCurrentUserName, loading: authLoading, isCoordinator, isVolunteer, isSuperAdmin } = useAuth();
  const { show: showToast, ToastEl } = useToast();
  const [page, setPage] = useState("dashboard");
  const [volunteers, setVolunteers] = useState([]);
  const [domains, setDomains] = useState([]);
  const [activities, setActivities] = useState([]);
  const [recurring, setRecurring] = useState([]);
  const [beneficiaries, setBeneficiaries] = useState([]);
  const [contributionTypes, setContributionTypes] = useState([]);
  const [users, setUsers] = useState([]);
  const [dataLoading, setDataLoading] = useState(true);
  const [selVol, setSelVol] = useState(null);
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= 900);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [quickOpen, setQuickOpen] = useState(false);
  const [openTokens, setOpenTokens] = useState({ activities: 0, recurring: 0, volunteers: 0, beneficiaries: 0, portal: 0 });
  const [pendingActivityDate, setPendingActivityDate] = useState("");
  const [pendingActivityInitial, setPendingActivityInitial] = useState(null);
  const [birthdayQueue, setBirthdayQueue] = useState([]);
  const [birthdayModalDismissed, setBirthdayModalDismissed] = useState(false);
  const [birthdayDoneMap, setBirthdayDoneMap] = useState({});
  const [greetingText, setGreetingText] = useState("");
  const [editNameOpen, setEditNameOpen] = useState(false);
  const [editName, setEditName] = useState("");
  const [editNameSaving, setEditNameSaving] = useState(false);
  const [openDevelopmentNotesCount, setOpenDevelopmentNotesCount] = useState(0);
  const [dismissedPendingRecurringCount, setDismissedPendingRecurringCount] = useState(0);
  const realtimeRefreshTimeoutRef = useRef(null);
  const routeRef = useRef({ page: "dashboard", selVol: null });
  const pendingQuickReturnRef = useRef(null);
  const skipHistoryPushRef = useRef(false);
  const PRIMARY_ADMIN_EMAIL = "studiozula148@gmail.com";

  const withTimeout = useCallback((promise, label, ms = 15000) => {
    const timeout = new Promise((_, reject) => {
      setTimeout(() => reject(new Error(`Timeout while loading ${label}`)), ms);
    });
    return Promise.race([promise, timeout]);
  }, []);

  const normalizedUserEmail = (userData?.email || "").trim().toLowerCase();
  const isPrimaryAdmin = normalizedUserEmail === PRIMARY_ADMIN_EMAIL;
  const userPermissions = userData?.permissions && typeof userData.permissions === "object" ? userData.permissions : {};
  const getPermission = useCallback((key) => {
    if (isSuperAdmin || userData?.isSuperAdmin === true || isPrimaryAdmin) return true;
    const defaults = isCoordinator ? COORDINATOR_ACTION_DEFAULTS : VOLUNTEER_ACTION_DEFAULTS;
    if (Object.prototype.hasOwnProperty.call(userPermissions, key)) return userPermissions[key] !== false;
    return defaults[key] === true;
  }, [isCoordinator, isPrimaryAdmin, isSuperAdmin, userData?.isSuperAdmin, userPermissions]);
  const canManageUsers = getPermission("manageUsers");
  const canAddVolunteers = getPermission("addVolunteers");
  const canAddActivities = getPermission("addActivities");
  const canAddRecurring = getPermission("addRecurring");
  const canAddContributionTypes = getPermission("addContributionTypes");
  const canEditDomains = getPermission("editDomains");
  const canAddBeneficiaries = getPermission("addBeneficiaries");
  const canViewReports = getPermission("viewReports");
  const canViewDevelopmentNotes = getPermission("viewDevelopmentNotes");
  const canManageSuperAdmin = isPrimaryAdmin || userData?.isSuperAdmin === true;
  const pendingRecurringApprovals = useMemo(
    () => recurring.filter((item) => (item?.approvalStatus || "pending") !== "approved").length,
    [recurring]
  );

  useEffect(() => {
    if (!currentUser || !canViewDevelopmentNotes) {
      setOpenDevelopmentNotesCount(0);
      return undefined;
    }

    const notesQuery = query(
      collection(db, "developmentNotes"),
      where("status", "==", "open")
    );

    return onSnapshot(
      notesQuery,
      (snapshot) => {
        setOpenDevelopmentNotesCount(snapshot.size);
      },
      (error) => {
        console.warn("Development notes counter subscription failed:", error);
      }
    );
  }, [currentUser, canViewDevelopmentNotes]);

  const applyRoute = useCallback((nextPage, options = {}) => {
    const nextSelVol = Object.prototype.hasOwnProperty.call(options, "selVol") ? options.selVol : routeRef.current.selVol;
    routeRef.current = { page: nextPage, selVol: nextSelVol || null };
    setSelVol(nextSelVol || null);
    setPage(nextPage);
  }, []);

  const navigateTo = useCallback((nextPage, options = {}) => {
    const state = {
      [APP_HISTORY_MARKER]: true,
      page: nextPage,
      selVol: Object.prototype.hasOwnProperty.call(options, "selVol") ? options.selVol || null : routeRef.current.selVol,
    };
    skipHistoryPushRef.current = true;
    applyRoute(nextPage, options);
    if (typeof window !== "undefined") {
      if (options.replace) window.history.replaceState(state, "");
      else window.history.pushState(state, "");
    }
    window.setTimeout(() => {
      skipHistoryPushRef.current = false;
    }, 0);
  }, [applyRoute]);

  const defaultHomePage = isCoordinator ? "dashboard" : "portal";

  const goBackInApp = useCallback((fallbackPage = defaultHomePage) => {
    if (typeof window === "undefined") return;
    const previousState = window.history.state;
    if (previousState && previousState[APP_HISTORY_MARKER]) {
      window.history.back();
      return;
    }
    navigateTo(fallbackPage, { replace: true, selVol: null });
  }, [defaultHomePage, navigateTo]);

  const loadData = useCallback(async (options = {}) => {
    const silent = options.silent === true;
    let hasError = false;
    try {
      if (!silent) setDataLoading(true);
      if (isCoordinator && !silent) {
        try {
          await withTimeout(syncRecurringActivitiesUntilToday(), "sync recurring activities", 25000);
        } catch (e) {
          console.warn("Recurring sync warning:", e);
        }
      }
      const shouldLoadUsers = canManageUsers || isCoordinator;
      const [vols, doms, acts, recs, usr, contributionTypeRows] = await Promise.all([
        withTimeout(getVolunteers(), "volunteers"),
        withTimeout(getDomains(), "domains"),
        withTimeout(getActivities(), "activities"),
        withTimeout(getRecurringActivities(), "recurring activities"),
        shouldLoadUsers ? withTimeout(getUsers(), "users") : Promise.resolve([]),
        withTimeout(getContributionTypes(), "contribution types"),
      ]);
      let myVolunteerId = userData?.volunteerId || null;
      if (!myVolunteerId && userData?.role === "volunteer") {
        const byEmail = vols.find((v) => ((v.email || "").trim().toLowerCase()) === ((userData.email || "").trim().toLowerCase()));
        myVolunteerId = byEmail?.id || null;
      }
      if (!myVolunteerId && userData?.role === "volunteer") {
        const byName = vols.filter((v) => (v.name || "").trim() === (userData.name || "").trim());
        if (byName.length === 1) myVolunteerId = byName[0].id;
      }

      const ben = isCoordinator
        ? await withTimeout(getBeneficiaries(), "beneficiaries")
        : await withTimeout(getBeneficiariesForVolunteer(myVolunteerId), "my beneficiaries");
      setVolunteers(vols);
      setDomains(doms);
      setActivities(acts);
      setRecurring(recs);
      setBeneficiaries(ben);
      setContributionTypes(contributionTypeRows);
      setUsers(usr);
    } catch (e) {
      hasError = true;
      console.error("Error loading:", e);
      showToast("שגיאה בטעינת נתונים. בדוק הרשאות ונסה לרענן.");
    } finally {
      if (!silent) setDataLoading(false);
      if (hasError) {
        setVolunteers((v) => v || []);
        setDomains((d) => d || []);
        setActivities((a) => a || []);
        setRecurring((r) => r || []);
        setBeneficiaries((b) => b || []);
        setContributionTypes((c) => c || []);
        setUsers((u) => u || []);
      }
    }
  }, [showToast, withTimeout, isCoordinator, userData, canManageUsers]);
  const refreshData = useCallback(() => loadData({ silent: true }), [loadData]);

  useEffect(() => {
    if (currentUser && userData) {
      loadData();
      seedInitialData()
        .then(s => { if (s) refreshData(); })
        .catch((e) => console.error("Seed failed:", e));
      if (userData.role === "volunteer") navigateTo("portal", { replace: true, selVol: null });
      if (userData.role === "coordinator" || userData.role === "group_coordinator") {
        navigateTo(routeRef.current.page === "portal" ? "dashboard" : routeRef.current.page, { replace: true, selVol: routeRef.current.selVol });
      }
    }
  }, [currentUser, userData, loadData, navigateTo, refreshData]);

  useEffect(() => {
    if (!currentUser || !userData) return undefined;
    const scheduleRefresh = () => {
      if (realtimeRefreshTimeoutRef.current) clearTimeout(realtimeRefreshTimeoutRef.current);
      realtimeRefreshTimeoutRef.current = setTimeout(() => {
        refreshData();
      }, 350);
    };

    const unsubs = [
      onSnapshot(collection(db, "activities"), scheduleRefresh),
      onSnapshot(collection(db, "volunteers"), scheduleRefresh),
      onSnapshot(collection(db, "beneficiaries"), scheduleRefresh),
      onSnapshot(collection(db, "contributionTypes"), scheduleRefresh),
      onSnapshot(collection(db, "recurringActivities"), scheduleRefresh),
    ];

    return () => {
      unsubs.forEach((u) => u());
      if (realtimeRefreshTimeoutRef.current) {
        clearTimeout(realtimeRefreshTimeoutRef.current);
        realtimeRefreshTimeoutRef.current = null;
      }
    };
  }, [currentUser, userData, refreshData]);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 900);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(BIRTHDAY_DONE_STORAGE_KEY);
      setBirthdayDoneMap(raw ? JSON.parse(raw) : {});
    } catch (_) {
      setBirthdayDoneMap({});
    }
  }, []);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(PENDING_RECURRING_ALERT_KEY);
      setDismissedPendingRecurringCount(Number(raw || 0) || 0);
    } catch (_) {
      setDismissedPendingRecurringCount(0);
    }
  }, []);

  useEffect(() => {
    if (!isCoordinator || !volunteers.length) {
      setBirthdayQueue([]);
      return;
    }
    const upcoming = buildUpcomingHebrewDates(new Date(), 7);

    const queue = volunteers
      .filter((v) => (v.status || "active") === "active")
      .map((v) => {
        const matched = findUpcomingBirthdayMatch(v, upcoming);
        if (!matched) return false;
        const doneKey = `${v.id}_${matched.year}`;
        if (birthdayDoneMap[doneKey] === true) return false;
        return { ...v, birthdayDoneKey: doneKey };
      })
      .filter(Boolean);
    setBirthdayQueue(queue);
  }, [isCoordinator, volunteers, birthdayDoneMap]);

  useEffect(() => {
    setBirthdayModalDismissed(false);
  }, [birthdayQueue.map((item) => item.birthdayDoneKey || item.id).join("|")]);

  useEffect(() => {
    setMobileNavOpen(false);
  }, [page]);

  useEffect(() => {
    if (typeof window === "undefined" || !currentUser || !userData) return undefined;
    const currentState = window.history.state;
    if (!currentState || !currentState[APP_HISTORY_MARKER]) {
      window.history.replaceState({ [APP_HISTORY_MARKER]: true, page: routeRef.current.page, selVol: routeRef.current.selVol }, "");
    }
    const handlePopState = (event) => {
      const state = event.state;
      if (state && state[APP_HISTORY_MARKER]) {
        skipHistoryPushRef.current = true;
        applyRoute(state.page || defaultHomePage, { selVol: state.selVol || null });
        window.setTimeout(() => {
          skipHistoryPushRef.current = false;
        }, 0);
        return;
      }
      navigateTo(defaultHomePage, { replace: true, selVol: null });
    };
    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, [applyRoute, currentUser, defaultHomePage, navigateTo, userData]);

  const bumpToken = (key) => setOpenTokens((p) => ({ ...p, [key]: (p[key] || 0) + 1 }));

  const runQuickAction = (action, options = {}) => {
    setQuickOpen(false);
    if (action === "activity") {
      setPendingActivityDate((options?.date || "").trim());
      setPendingActivityInitial(options?.initial || null);
      const targetKey = isCoordinator ? "activities" : "portal";
      pendingQuickReturnRef.current = routeRef.current.page;
      navigateTo(targetKey, { selVol: null });
      window.setTimeout(() => bumpToken(targetKey), 0);
      return;
    }
    if (action === "recurring") {
      pendingQuickReturnRef.current = routeRef.current.page;
      navigateTo("recurring", { selVol: null });
      window.setTimeout(() => bumpToken("recurring"), 0);
      return;
    }
    if (action === "volunteer") {
      pendingQuickReturnRef.current = routeRef.current.page;
      navigateTo("volunteers", { selVol: null });
      window.setTimeout(() => bumpToken("volunteers"), 0);
      return;
    }
    if (action === "beneficiary") {
      pendingQuickReturnRef.current = routeRef.current.page;
      navigateTo("beneficiaries", { selVol: null });
      window.setTimeout(() => bumpToken("beneficiaries"), 0);
    }
  };

  const handleQuickAddCompleted = useCallback(() => {
    const targetPage = pendingQuickReturnRef.current || defaultHomePage;
    pendingQuickReturnRef.current = null;
    navigateTo(targetPage, { selVol: null });
  }, [defaultHomePage, navigateTo]);

  const openNameEditor = () => {
    setEditName((userData?.name || "").trim());
    setEditNameOpen(true);
  };

  const saveEditedName = async () => {
    const nextName = (editName || "").trim();
    if (!nextName) {
      await showStyledAlert("יש להזין שם מלא.");
      return;
    }
    setEditNameSaving(true);
    try {
      await updateCurrentUserName(nextName);
      showToast("השם עודכן בהצלחה");
      setEditNameOpen(false);
      await refreshData();
    } catch (e) {
      await showStyledAlert(`שגיאה בעדכון שם: ${e.message}`);
    }
    setEditNameSaving(false);
  };

  const markBirthdayDone = (volunteer) => {
    const fallbackMatch = findUpcomingBirthdayMatch(volunteer, buildUpcomingHebrewDates(new Date(), 7));
    const key = volunteer?.birthdayDoneKey || (fallbackMatch ? `${volunteer.id}_${fallbackMatch.year}` : "");
    if (!key) return;
    const next = { ...birthdayDoneMap, [key]: true };
    setBirthdayDoneMap(next);
    try {
      localStorage.setItem(BIRTHDAY_DONE_STORAGE_KEY, JSON.stringify(next));
    } catch (_) {
      // no-op
    }
  };

  const dismissPendingRecurringAlert = useCallback((count = pendingRecurringApprovals) => {
    setDismissedPendingRecurringCount(count);
    try {
      localStorage.setItem(PENDING_RECURRING_ALERT_KEY, String(count));
    } catch (_) {
      // no-op
    }
  }, [pendingRecurringApprovals]);

  const navigateFromMenu = useCallback((nextPage, options = {}) => {
    setMobileNavOpen(false);
    setQuickOpen(false);
    navigateTo(nextPage, options);
  }, [navigateTo]);

  const buildGreeting = (volunteer) => {
    const dateLabel = volunteer.hebrewBirthday || "";
    const text =
      `לכבוד ${fullVolunteerName(volunteer)},\n\n` +
      "מזל טוב ליום הולדתך!\n" +
      "תודה על הלב הגדול, הנתינה והעשייה שלך בעזר מציון.\n" +
      "מאחלים לך בריאות, שמחה ושפע הצלחה בכל מעשיך.\n\n" +
      `יום הולדת עברי: ${dateLabel}\n` +
      "באהבה,\nצוות מחוז דרום";
    setGreetingText(text);
  };

  if (authLoading) return <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100vh" }}><LoadingSpinner /></div>;
  if (!currentUser || !userData) return <LoginPage />;

  const allKnownUsers = users.length ? users : (userData ? [userData] : []);
  const regularCoordinators = allKnownUsers.filter((u) => u.role === "coordinator");
  const coordinatorLikeUsers = allKnownUsers.filter((u) => u.role === "coordinator" || u.role === "group_coordinator");
  const roleLabel = isCoordinator ? "רכז" : "מתנדב";

  const navItems = isCoordinator ? [
    { id: "dashboard", label: "דשבורד", icon: Ic.Home },
    { id: "volunteers", label: "מתנדבים", icon: Ic.Users },
    { id: "activities", label: "פעילות", icon: Ic.Zap },
    { id: "contributions", label: "תרומות", icon: Ic.Gift },
    { id: "recurring", label: "קבועות", icon: Ic.Repeat, badgeCount: canManageSuperAdmin ? pendingRecurringApprovals : 0 },
    { id: "journal", label: "יומן", icon: Ic.Calendar },
    { id: "domains", label: "תחומים", icon: Ic.Grid },
    { id: "beneficiaries", label: "נעזרים", icon: Ic.User },
    ...(canViewDevelopmentNotes ? [{ id: "development-notes", label: "הערות לפיתוח", icon: Ic.Note, badgeCount: openDevelopmentNotesCount }] : []),
    ...(canManageUsers ? [{ id: "users", label: "משתמשים", icon: Ic.Settings }] : []),
    ...(canViewReports ? [{ id: "reports", label: "דוחות", icon: Ic.Chart }] : []),
  ] : [
    { id: "portal", label: "האזור שלי", icon: Ic.User },
    ...(canViewDevelopmentNotes ? [{ id: "development-notes", label: "הערות לפיתוח", icon: Ic.Note, badgeCount: openDevelopmentNotesCount }] : []),
  ];

  const titles = { dashboard: "דשבורד", volunteers: "ניהול מתנדבים מחוז דרום", activities: "פעילות", contributions: "תרומות", recurring: "פעילויות קבועות", journal: "יומן", domains: "תחומי פעילות", beneficiaries: "ניהול נעזרים", users: "ניהול משתמשים", reports: "דוחות", portal: "הפורטל שלי", "vol-detail": "פרטי מתנדב", "development-notes": "הערות לפיתוח" };
  const normalizedUserName = (userData.name || "").trim();
  const nameMatches = volunteers.filter((v) => (v.name || "").trim() === normalizedUserName);
  const myVolunteer =
    volunteers.find((v) => v.id === userData.volunteerId) ||
    volunteers.find((v) => ((v.email || "").trim().toLowerCase()) === normalizedUserEmail) ||
    (nameMatches.length === 1 ? nameMatches[0] : null) ||
    null;
  const mobileQuickNav = isCoordinator
    ? [
      { id: "dashboard", label: "דשבורד", icon: Ic.Home },
      { id: "volunteers", label: "מתנדבים", icon: Ic.Users },
      { id: "journal", label: "יומן", icon: Ic.Calendar },
      { id: "contributions", label: "תרומות", icon: Ic.Gift },
      ...(canViewReports ? [{ id: "reports", label: "דוחות", icon: Ic.Chart }] : [{ id: "development-notes", label: "פיתוח", icon: Ic.Note, badgeCount: openDevelopmentNotesCount }]),
    ]
    : [
      { id: "portal", label: "האזור שלי", icon: Ic.User },
      ...(canViewDevelopmentNotes ? [{ id: "development-notes", label: "פיתוח", icon: Ic.Note, badgeCount: openDevelopmentNotesCount }] : []),
    ];

  const sideContent = (
    <>
      <div style={{ padding: sidebarCollapsed ? "20px 10px" : "20px 16px", borderBottom: "1px solid rgba(255,255,255,0.08)", display: "flex", alignItems: "center", gap: 12, justifyContent: sidebarCollapsed ? "center" : "flex-start" }}>
        <div style={{ width: 36, height: 36, borderRadius: 10, background: "white", border: "1px solid rgba(255,255,255,0.25)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, overflow: "hidden" }}>
          <img src="/logo.png" alt="לוגו עזר מציון" style={{ width: "100%", height: "100%", objectFit: "contain" }} />
        </div>
        {!sidebarCollapsed && <div><div style={{ fontFamily: "Assistant", fontSize: 18, fontWeight: 800 }}>עזר מציון</div><div style={{ fontSize: 11, color: "rgba(255,255,255,0.5)" }}>ניהול מתנדבים מחוז דרום</div></div>}
      </div>
      <nav style={{ flex: 1, padding: sidebarCollapsed ? "16px 8px" : "16px 10px", display: "flex", flexDirection: "column", gap: 6 }}>
      {navItems.map(item => (
          <button
            key={item.id}
            onClick={() => navigateFromMenu(item.id, { selVol: null })}
            title={sidebarCollapsed ? item.label : ""}
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: sidebarCollapsed ? "center" : "flex-start",
              gap: 12,
              padding: sidebarCollapsed ? "11px 10px" : "11px 14px",
              borderRadius: 10,
              cursor: "pointer",
              color: page === item.id ? "white" : "rgba(255,255,255,0.6)",
              fontSize: 14,
              fontWeight: page === item.id ? 500 : 400,
              background: page === item.id ? "rgba(196,30,58,0.2)" : "transparent",
              border: "none",
              width: "100%",
              textAlign: "right",
              direction: "rtl",
            }}
          >
            <item.icon />
            {!sidebarCollapsed && (
              <>
                <span style={{ flex: 1, minWidth: 0 }}>{item.label}</span>
                <NavCounter count={item.badgeCount} active={page === item.id} />
              </>
            )}
          </button>
        ))}
      </nav>
      <div style={{ padding: sidebarCollapsed ? 10 : 16, borderTop: "1px solid rgba(255,255,255,0.08)" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, justifyContent: sidebarCollapsed ? "center" : "space-between" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0, flex: 1 }}>
            <div style={{ width: 34, height: 34, borderRadius: 8, background: "linear-gradient(135deg,#C41E3A,#E8364F)", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700, fontSize: 13, flexShrink: 0 }}>{userData.name?.[0] || "?"}</div>
            {!sidebarCollapsed && (
              <div style={{ minWidth: 0, flex: 1 }}>
                <div style={{ fontSize: 13, fontWeight: 600, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{userData.name}</div>
                <div style={{ fontSize: 11, color: "rgba(255,255,255,0.4)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{roleLabel}</div>
              </div>
            )}
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 6, flexShrink: 0 }}>
            <button
              onClick={() => {
                setMobileNavOpen(false);
                openNameEditor();
              }}
              title="עריכת שם"
              aria-label="עריכת שם"
              style={{ ...btnGhost, width: 34, height: 34, padding: 0, justifyContent: "center", color: "rgba(255,255,255,0.72)", borderRadius: 10 }}
            >
              <Ic.Edit />
            </button>
            <button
              onClick={() => {
                setMobileNavOpen(false);
                logout();
              }}
              title="יציאה"
              aria-label="יציאה"
              style={{ ...btnGhost, width: 34, height: 34, padding: 0, justifyContent: "center", color: "rgba(255,255,255,0.5)", borderRadius: 10 }}
            >
              <Ic.LogOut />
            </button>
          </div>
        </div>
      </div>
    </>
  );

  return (
    <div style={{ display: "flex", height: "100vh", direction: "rtl", flexDirection: isMobile ? "column" : "row" }}>
      {!isMobile && (
        <aside style={{ width: sidebarCollapsed ? 76 : 240, minWidth: sidebarCollapsed ? 76 : 240, transition: "width .2s ease", background: "linear-gradient(195deg,#171717 0%,#1a1a2e 100%)", color: "white", display: "flex", flexDirection: "column" }}>
          {sideContent}
        </aside>
      )}

      {isMobile && (
        <>
          <div style={{ height: 58, background: "white", borderBottom: "1px solid #E8E8E8", display: "flex", alignItems: "center", justifyContent: "space-between", padding: "0 12px", flexShrink: 0 }}>
            <button onClick={() => setMobileNavOpen(true)} style={{ ...btnSecondary, padding: "7px 10px" }}><Ic.Grid />תפריט</button>
            <span style={{ fontFamily: "Assistant", fontSize: 18, fontWeight: 800, color: "#171717" }}>{titles[page] || ""}</span>
            <div style={{ width: 34, height: 34, borderRadius: 8, background: "linear-gradient(135deg,#C41E3A,#E8364F)", color: "white", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700, fontSize: 13 }}>{userData.name?.[0] || "?"}</div>
          </div>
          {mobileNavOpen && (
            <div style={{ position: "fixed", inset: 0, zIndex: 90, background: "rgba(0,0,0,0.45)" }} onClick={() => setMobileNavOpen(false)}>
              <aside style={{ width: 270, maxWidth: "85vw", height: "100%", background: "linear-gradient(195deg,#171717 0%,#1a1a2e 100%)", color: "white", display: "flex", flexDirection: "column" }} onClick={(e) => e.stopPropagation()}>
                {sideContent}
              </aside>
            </div>
          )}
        </>
      )}

      <main style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
        {!isMobile && <div style={{ height: 64, background: "white", borderBottom: "1px solid #E8E8E8", display: "flex", alignItems: "center", justifyContent: "space-between", padding: "0 28px", flexShrink: 0 }}>
          <span style={{ fontFamily: "Assistant", fontSize: 22, fontWeight: 700, color: "#171717" }}>{titles[page] || ""}</span>
          <button
            onClick={() => setSidebarCollapsed((prev) => !prev)}
            style={{ ...btnSecondary, padding: "8px 12px", gap: 6 }}
            title={sidebarCollapsed ? "הרחב תפריט" : "כווץ תפריט"}
          >
            <Ic.Grid />
            {sidebarCollapsed ? "הרחב תפריט" : "כווץ תפריט"}
          </button>
        </div>}
        <div style={{ flex: 1, overflowY: "auto", padding: isMobile ? "12px 12px 84px" : "24px 28px" }}>
          {dataLoading ? <LoadingSpinner /> : <>
            {page === "dashboard" && isCoordinator && <DashboardPage volunteers={volunteers} domains={domains} activities={activities} beneficiaries={beneficiaries} onViewVolunteer={id => navigateFromMenu("vol-detail", { selVol: id })} isMobile={isMobile} coordinators={coordinatorLikeUsers} />}
            {page === "volunteers" && isCoordinator && <VolunteersPage volunteers={volunteers} domains={domains} activities={activities} onView={id => navigateFromMenu("vol-detail", { selVol: id })} onRefresh={refreshData} showToast={showToast} isMobile={isMobile} addOpenToken={openTokens.volunteers} users={allKnownUsers} currentUserData={userData} canManageUsers={canManageUsers} canAddVolunteers={canAddVolunteers} onQuickAddComplete={handleQuickAddCompleted} />}
            {page === "vol-detail" && isCoordinator && <VolDetailInline volId={selVol} volunteers={volunteers} domains={domains} activities={activities} recurring={recurring} onBack={() => goBackInApp("volunteers")} />}
            {page === "activities" && isCoordinator && <ActivitiesPage volunteers={volunteers} domains={domains} beneficiaries={beneficiaries} activities={activities} users={allKnownUsers} contributionTypes={contributionTypes} onRefresh={refreshData} showToast={showToast} userId={userData.id} isMobile={isMobile} addOpenToken={openTokens.activities} addInitialDate={pendingActivityDate} addInitial={pendingActivityInitial} canAddActivities={canAddActivities} onQuickAddComplete={handleQuickAddCompleted} />}
            {page === "contributions" && isCoordinator && <ContributionsPage contributionTypes={contributionTypes} activities={activities} onRefresh={refreshData} showToast={showToast} isMobile={isMobile} canEdit={canAddContributionTypes} />}
            {page === "recurring" && isCoordinator && <RecurringPage recurring={recurring} volunteers={volunteers} beneficiaries={beneficiaries} domains={domains} contributionTypes={contributionTypes} onRefresh={refreshData} showToast={showToast} isMobile={isMobile} addOpenToken={openTokens.recurring} canAddRecurring={canAddRecurring} canApproveRecurring={canManageSuperAdmin} currentUser={userData} onQuickAddComplete={handleQuickAddCompleted} />}
            {page === "journal" && isCoordinator && <JournalPage users={users} activities={activities} volunteers={volunteers} recurring={recurring} domains={domains} isMobile={isMobile} onAddActivity={(date) => runQuickAction("activity", { date })} />}
            {page === "domains" && isCoordinator && <DomainsPage domains={domains} activities={activities} onRefresh={refreshData} showToast={showToast} isMobile={isMobile} canEditDomains={canEditDomains} />}
            {page === "beneficiaries" && isCoordinator && <BeneficiariesPage volunteers={volunteers} domains={domains} activities={activities} recurring={recurring} showToast={showToast} isMobile={isMobile} addOpenToken={openTokens.beneficiaries} coordinators={regularCoordinators} currentUser={userData} canAddBeneficiaries={canAddBeneficiaries} canAddActivities={canAddActivities} onAddActivity={(initial) => runQuickAction("activity", { initial })} onQuickAddComplete={handleQuickAddCompleted} />}
            {page === "users" && canManageUsers && <UsersPage users={users} volunteers={volunteers} domains={domains} onRefresh={refreshData} showToast={showToast} currentUserId={userData?.id} isMobile={isMobile} canManageSuperAdmin={canManageSuperAdmin} />}
            {page === "reports" && isCoordinator && canViewReports && <ReportsPage volunteers={volunteers} domains={domains} activities={activities} isMobile={isMobile} />}
            {page === "development-notes" && <DevelopmentNotesPage currentUser={userData} showToast={showToast} isMobile={isMobile} />}
            {page === "portal" && isVolunteer && <PortalPage volunteer={myVolunteer} domains={domains} activities={activities} recurring={recurring} beneficiaries={beneficiaries} contributionTypes={contributionTypes} onRefresh={refreshData} showToast={showToast} userId={userData.id} isMobile={isMobile} addOpenToken={openTokens.portal} />}
          </>}
        </div>
      </main>
      <button
        onClick={() => {
          if (page === "activities" && canAddActivities) return runQuickAction("activity");
          if (page === "recurring" && canAddRecurring) return runQuickAction("recurring");
          if (page === "volunteers" && canAddVolunteers) return runQuickAction("volunteer");
          if (page === "beneficiaries" && canAddBeneficiaries) return runQuickAction("beneficiary");
          if (page === "portal") return runQuickAction("activity");
          setQuickOpen(true);
        }}
        style={{
          position: "fixed",
          left: 16,
          bottom: isMobile ? 86 : 20,
          width: 58,
          height: 58,
          borderRadius: "50%",
          border: "none",
          background: "linear-gradient(135deg,#C41E3A,#E8364F)",
          color: "white",
          boxShadow: "0 10px 24px rgba(196,30,58,0.35)",
          zIndex: 95,
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Ic.Plus />
      </button>
      {quickOpen && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.35)", zIndex: 96 }} onClick={() => setQuickOpen(false)}>
          <div style={{ position: "absolute", left: 12, bottom: isMobile ? 146 : 88, width: 250, background: "white", borderRadius: 14, border: "1px solid #E8E8E8", boxShadow: "0 14px 34px rgba(0,0,0,0.22)", padding: 10 }} onClick={(e) => e.stopPropagation()}>
            <div style={{ fontFamily: "Assistant", fontSize: 18, fontWeight: 800, marginBottom: 8, padding: "6px 8px" }}>הוספה מהירה</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {(isVolunteer || canAddActivities) && <button style={btnSecondary} onClick={() => runQuickAction("activity")}><Ic.Zap />{isCoordinator ? "פעילות חדשה" : "דיווח פעילות"}</button>}
              {isCoordinator && canAddRecurring && <button style={btnSecondary} onClick={() => runQuickAction("recurring")}><Ic.Repeat />פעילות קבועה</button>}
              {isCoordinator && canAddVolunteers && <button style={btnSecondary} onClick={() => runQuickAction("volunteer")}><Ic.Users />מתנדב חדש</button>}
              {isCoordinator && canAddBeneficiaries && <button style={btnSecondary} onClick={() => runQuickAction("beneficiary")}><Ic.User />נעזר חדש</button>}
            </div>
          </div>
        </div>
      )}
      {isMobile && (
        <div style={{ position: "fixed", left: 0, right: 0, bottom: 0, zIndex: 80, background: "rgba(255,255,255,0.97)", borderTop: "1px solid #E8E8E8", backdropFilter: "blur(10px)", display: "grid", gridTemplateColumns: `repeat(${mobileQuickNav.length + 1},1fr)`, padding: "6px 8px 8px" }}>
          {mobileQuickNav.map((item) => (
            <button key={item.id} onClick={() => navigateFromMenu(item.id, { selVol: null })} style={{ border: "none", background: "none", padding: "6px 4px", display: "flex", flexDirection: "column", alignItems: "center", gap: 4, color: page === item.id ? "#C41E3A" : "#737373", fontFamily: "Heebo", fontSize: 11, fontWeight: page === item.id ? 700 : 500 }}>
              <div style={{ position: "relative", display: "inline-flex" }}>
                <item.icon />
                {!!item.badgeCount && (
                  <span style={{ position: "absolute", top: -8, left: -12 }}>
                    <NavCounter count={item.badgeCount} active={false} />
                  </span>
                )}
              </div>
              <span>{item.label}</span>
            </button>
          ))}
          <button onClick={() => setMobileNavOpen(true)} style={{ border: "none", background: "none", padding: "6px 4px", display: "flex", flexDirection: "column", alignItems: "center", gap: 4, color: "#737373", fontFamily: "Heebo", fontSize: 11, fontWeight: 500 }}>
            <Ic.Grid />
            <span>עוד</span>
          </button>
        </div>
      )}
      {canManageSuperAdmin && pendingRecurringApprovals > 0 && pendingRecurringApprovals !== dismissedPendingRecurringCount && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.42)", zIndex: 119, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div style={{ width: "min(92vw,520px)", background: "white", borderRadius: 16, border: "1px solid #E8E8E8", boxShadow: "0 24px 70px rgba(0,0,0,0.22)", overflow: "hidden" }}>
            <div style={{ padding: "14px 16px", borderBottom: "1px solid #E8E8E8", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
              <div>
                <div style={{ fontFamily: "Assistant", fontSize: 21, fontWeight: 800 }}>ממתין לאישור</div>
                <div style={{ fontSize: 13, color: "#525252", marginTop: 4 }}>יש {pendingRecurringApprovals} פעילויות קבועות שממתינות לאישור מנהל.</div>
              </div>
              <button onClick={() => dismissPendingRecurringAlert()} style={{ ...btnGhost, width: 34, height: 34, padding: 0, justifyContent: "center", borderRadius: 10 }} aria-label="סגירה">
                <Ic.X />
              </button>
            </div>
            <div style={{ padding: 16, display: "flex", gap: 8, justifyContent: "flex-end", flexWrap: "wrap" }}>
              <button
                style={btnPrimary}
                onClick={() => {
                  dismissPendingRecurringAlert();
                  navigateFromMenu("recurring", { selVol: null });
                }}
              >
                <Ic.Repeat />
                מעבר לפעילויות קבועות
              </button>
              <button style={btnSecondary} onClick={() => dismissPendingRecurringAlert()}>סגירה</button>
            </div>
          </div>
        </div>
      )}
      {isCoordinator && birthdayQueue.length > 0 && !birthdayModalDismissed && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.42)", zIndex: 120, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div style={{ width: "min(92vw,560px)", background: "white", borderRadius: 16, border: "1px solid #E8E8E8", boxShadow: "0 24px 70px rgba(0,0,0,0.22)", overflow: "hidden" }}>
            <div style={{ padding: "16px 18px", borderBottom: "1px solid #E8E8E8", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
              <div style={{ fontFamily: "Assistant", fontSize: 22, fontWeight: 800 }}>תזכורת ימי הולדת השבוע</div>
              <button onClick={() => setBirthdayModalDismissed(true)} style={{ ...btnGhost, width: 34, height: 34, padding: 0, justifyContent: "center", borderRadius: 10 }} aria-label="סגירה">
                <Ic.X />
              </button>
            </div>
            <div style={{ padding: 16, maxHeight: "60vh", overflowY: "auto", display: "grid", gap: 10 }}>
              {birthdayQueue.map((v) => (
                <div key={v.id} style={{ border: "1px solid #E8E8E8", borderRadius: 12, padding: 12 }}>
                  <div style={{ fontWeight: 800, marginBottom: 4 }}>{fullVolunteerName(v)}</div>
                  <div style={{ fontSize: 13, color: "#525252", marginBottom: 8 }}>יום הולדת עברי: {v.hebrewBirthday || "—"}</div>
                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                    <button style={btnSecondary} onClick={() => buildGreeting(v)}>צור ברכה</button>
                    <button style={btnSecondary} onClick={() => markBirthdayDone(v)}>בוצע</button>
                  </div>
                </div>
              ))}
            </div>
            <div style={{ padding: 12, borderTop: "1px solid #E8E8E8", display: "flex", justifyContent: "flex-end" }}>
              <button style={btnSecondary} onClick={() => setBirthdayModalDismissed(true)}>ביטול</button>
            </div>
          </div>
        </div>
      )}
      {greetingText && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.42)", zIndex: 121, display: "flex", alignItems: "center", justifyContent: "center" }} onClick={() => setGreetingText("")}>
          <div style={{ width: "min(92vw,560px)", background: "linear-gradient(140deg,#fff7ed,#fffbeb)", borderRadius: 16, border: "1px solid #FDE68A", boxShadow: "0 24px 70px rgba(0,0,0,0.22)", overflow: "hidden" }} onClick={(e) => e.stopPropagation()}>
            <div style={{ padding: "16px 18px", borderBottom: "1px solid #FDE68A", fontFamily: "Assistant", fontSize: 22, fontWeight: 800 }}>ברכת יום הולדת</div>
            <div style={{ padding: 16, whiteSpace: "pre-wrap", lineHeight: 1.7, fontSize: 15 }}>{greetingText}</div>
            <div style={{ padding: 12, borderTop: "1px solid #FDE68A", display: "flex", gap: 8, justifyContent: "flex-end" }}>
              <button style={btnSecondary} onClick={() => setGreetingText("")}>סגירה</button>
              <button style={btnSecondary} onClick={() => navigator.clipboard?.writeText(greetingText)}>העתקה</button>
            </div>
          </div>
        </div>
      )}
      {editNameOpen && (
        <Modal
          title="עריכת שם"
          onClose={() => !editNameSaving && setEditNameOpen(false)}
          footer={(
            <>
              <button style={btnPrimary} onClick={saveEditedName} disabled={editNameSaving}>
                <Ic.Check />
                {editNameSaving ? "שומר..." : "שמירה"}
              </button>
              <button style={btnSecondary} onClick={() => setEditNameOpen(false)} disabled={editNameSaving}>ביטול</button>
            </>
          )}
        >
          <div style={{ marginBottom: 12 }}>
            <label style={{ display: "block", fontSize: 13, fontWeight: 600, color: "#525252", marginBottom: 6 }}>שם מלא</label>
            <input
              style={inputStyle}
              value={editName}
              onChange={(e) => setEditName(e.target.value)}
              disabled={editNameSaving}
              placeholder="הזן שם מלא"
            />
          </div>
        </Modal>
      )}
      {ToastEl}
    </div>
  );
}

export default function App() {
  return (
    <>
      <style>{css}</style>
      <AuthProvider>
        <AppContent />
      </AuthProvider>
    </>
  );
}
